import { ColDef } from "@ag-grid-community/core";
import { useFeeChargeEventPointInTimeTransactionCodesListSuspense } from "gen/endpoints/fee-charge-event/fee-charge-event";
import { useInvestmentListSuspense } from "gen/endpoints/investment/investment";
import { PortfolioTransactionBulkCreateDataRequest } from "gen/model/portfolioTransactionBulkCreateDataRequest";
import { PortfolioTransactionCreateData } from "gen/model/portfolioTransactionCreateData";
import { PortfolioTransactionReadData } from "gen/model/portfolioTransactionReadData";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MaybernTable } from "shared/components/table/maybern/MaybernTable";
import { MaybernTableAddRemoveCellRenderer } from "shared/components/table/maybern/MaybernTableAddRemoveCellRenderer";
import { MaybernTableCellRenderer } from "shared/components/table/maybern/MaybernTableCellRenderer";
import { MaybernTableDateCellRenderer } from "shared/components/table/maybern/MaybernTableDateCellRenderer";
import { MaybernTableGetErrorForCellFn } from "shared/components/table/maybern/MaybernTableErrors";
import { useMaybernTableFormRowController } from "shared/components/table/maybern/MaybernTableFormRowController";
import { MaybernTableRowWithId } from "shared/components/table/maybern/MaybernTableRowController";

export type BasisAdjustmentsTableRow =
  | MaybernTableRowWithId<PortfolioTransactionCreateData>
  | PortfolioTransactionReadData;

export const makeEmptyBasisAdjustmentsRow = (): BasisAdjustmentsTableRow => ({
  id: crypto.randomUUID(),
  amount: "",
  effective_date: "",
  fund_family_event_id: "",
  transaction_code_id: "",
});

export const FeePostUseBreakdownBasisAdjustmentsTable: React.FC<{
  fundFamilyId: string;
  getErrorForCell: MaybernTableGetErrorForCellFn<BasisAdjustmentsTableRow>;
  form: ReturnType<typeof useForm<PortfolioTransactionBulkCreateDataRequest>>;
}> = ({ fundFamilyId, getErrorForCell, form }) => {
  const { t } = useTranslation(["fees", "common"]);
  const { propsForRows, addRow, removeRow } = useMaybernTableFormRowController(
    form,
    "portfolio_transactions",
  );

  const { data: transactionCodeList } =
    useFeeChargeEventPointInTimeTransactionCodesListSuspense(fundFamilyId);

  const { data: investmentListData } = useInvestmentListSuspense(fundFamilyId, {
    is_allocable: true,
  });

  const columnDefs = useMemo(
    (): ColDef<BasisAdjustmentsTableRow>[] => [
      {
        colId: "addRemove",
        width: 94,
        resizable: false,
        cellClassRules: {
          "mb-cell-is-left-actions": () => true,
        },
        cellRenderer: MaybernTableAddRemoveCellRenderer,
        cellRendererParams: {
          onClickAdd: () => addRow(makeEmptyBasisAdjustmentsRow()),
          onClickRemove: removeRow,
        },
      },
      {
        field: "transaction_code_id",
        headerName: t(
          "fees:feeUseBreakdown.basisChangeTable.transactionCode.columnTitle",
        ),
        width: 300,
        type: ["dropdown"],
        cellRendererParams: {
          title: t(
            "fees:feeUseBreakdown.basisChangeTable.transactionCode.inputTitle",
          ),
          options: transactionCodeList.map(({ id, name }) => ({
            value: id,
            children: name,
          })),
        },
      },
      {
        field: "investment_id",
        headerName: t("fees:feeUseBreakdown.basisChangeTable.investment.title"),
        width: 260,
        type: ["dropdown"],
        cellRendererParams: {
          title: t("fees:feeUseBreakdown.basisChangeTable.investment.title"),
          options: investmentListData.map(({ id, legal_name }) => ({
            value: id,
            children: legal_name,
          })),
        },
      },
      {
        field: "amount",
        headerName: t("common:amount"),
        width: 180,
        headerClass: "mb-header-value-type-money mb-column-has-form-field",
        cellRenderer: MaybernTableCellRenderer,
      },
      {
        field: "effective_date",
        headerName: t("common:effectiveDate"),
        width: 180,
        cellRenderer: MaybernTableDateCellRenderer,
      },
    ],
    [addRow, investmentListData, removeRow, t, transactionCodeList],
  );

  return (
    <MaybernTable<BasisAdjustmentsTableRow>
      defaultColDef={defaultColDef}
      columnDefs={columnDefs}
      getErrorForCell={getErrorForCell}
      {...propsForRows}
    />
  );
};

const defaultColDef: ColDef = {
  headerClass: "mb-column-has-form-field",
  cellClassRules: {
    "mb-column-has-form-field": () => true,
  },
};
